import { h } from 'preact';
import { useState, useRef } from 'preact/hooks';
import handleize from 'utils/handleize';

const ColorSwatches = ({
  product,
  onClickSwatch,
  activeColor,
  availableColorsArray = [],
  isDisabled = false,
  maxSwatches,
  swatchSizes = 'w-3 h-3 md:w-4 md:h-4',
  align = 'justify-center',
}) => {
  const fallbackSwatchURL =
    product?.swatchURL?.reference?.image?.url || product?.swatchURL || null;

  if (!availableColorsArray.length && !fallbackSwatchURL) return null;

  const initialActiveColorRef = useRef(activeColor);
  const [currentActiveColor, setCurrentActiveColor] = useState(activeColor);

  const handleSwatchClick = (e, colorData, colorKey) => {
    e.preventDefault();
    setCurrentActiveColor(colorKey);
    onClickSwatch(colorData);
  };

  const initialIndex = availableColorsArray.findIndex(
    (color) => Object.keys(color)[0] === initialActiveColorRef.current,
  );

  const adjustedColors = (() => {
    if (initialIndex === -1) return availableColorsArray.slice(0, maxSwatches);

    let start = Math.max(0, initialIndex - Math.floor(maxSwatches / 2));
    let end = start + maxSwatches;

    if (end > availableColorsArray.length) {
      end = availableColorsArray.length;
      start = Math.max(0, end - maxSwatches);
    }

    const selectedColors = availableColorsArray.slice(start, end);
    if (
      !selectedColors.some(
        (color) => Object.keys(color)[0] === initialActiveColorRef.current,
      )
    ) {
      selectedColors.pop();
      selectedColors.push({
        [initialActiveColorRef.current]: Object.values(
          availableColorsArray[initialIndex],
        )[0],
      });
    }

    return selectedColors;
  })();

  return (
    <div
      // eslint-disable-next-line max-len
      className={`color-swatches flex gap-3.5 lg:gap-4 h-fit items-center md:min-h-[28px] ${align}`}
    >
      {availableColorsArray.length > 0 ? (
        adjustedColors.map((colorObj) => {
          const colorData = Object.values(colorObj)[0];
          const colorKey = Object.keys(colorObj)[0];

          return (
            <button
              key={`${product.id}-${colorKey}`}
              disabled={isDisabled}
              // eslint-disable-next-line max-len
              className={`swatch rounded-full ${swatchSizes} block cursor-pointer ${
                colorKey === currentActiveColor
                  ? 'ring-1 ring-offset-4 ring-black'
                  : ''
              }`}
              title={`Select ${handleize(colorData.colorFamily)} swatch`}
              style={{
                backgroundImage: colorData.image
                  ? `url(${colorData.image})`
                  : 'none',
              }}
              data-swatch={handleize(colorData.colorFamily)}
              onClick={(e) => handleSwatchClick(e, colorData, colorKey)}
            ></button>
          );
        })
      ) : (
        <button
          key={`${product.id}-fallback`}
          disabled={isDisabled}
          className={`swatch rounded-full ${swatchSizes} block cursor-pointer ${
            activeColor ? 'ring-1 ring-offset-4 ring-black' : ''
          }`}
          style={{ backgroundImage: `url(${fallbackSwatchURL})` }}
          data-swatch="fallback"
          title={`Select ${currentActiveColor} swatch`}
          onClick={(e) =>
            handleSwatchClick(
              e,
              { image: fallbackSwatchURL, colorFamily: 'default' },
              'fallback',
            )
          }
        ></button>
      )}
      {availableColorsArray.length > maxSwatches && (
        <span className="w-[18px] h-[18px] block body-3 leading-none lg:body-2">
          +{availableColorsArray.length - maxSwatches}
        </span>
      )}
    </div>
  );
};

export default ColorSwatches;
